import type { StepCategory } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, cn, Icon } from "@sagot/ui";
import type { AddStepEntry } from "./add-step-catalog";

const S = "engage.workflows.addStep";

/** Tile of each category (semantic tokens only; `--accent` is for signals, never for a step). */
const TILE: Record<StepCategory, string> = {
  content: "bg-primary-soft text-primary-ink",
  actions: "bg-success-soft text-success",
  flow: "border border-line-strong bg-sunken text-fg-muted",
};
/** Steps that use a model: the brand fill and the sparkles icon. */
const AI_TILE = "bg-primary text-on-primary";

export const optionIdOf = (panelId: string, section: string, entryId: string): string => `${panelId}-${section}-${entryId}`;

interface AddStepItemProps {
  entry: AddStepEntry;
  optionId: string;
  active: boolean;
  /** The section note that explains why the trigger locks this step. */
  noteId: string | undefined;
  onActivate: () => void;
  onChoose: () => void;
}

/**
 * One step of the menu. The option's name is its label only; the category and the lock go in its description, so a screen
 * reader says "Data connector, Actions, needs the Pro plan". A locked step stays in the list (and in the keyboard order)
 * with `aria-disabled`, so it is announced instead of silently missing.
 */
export function AddStepItem({ entry, optionId, active, noteId, onActivate, onChoose }: AddStepItemProps) {
  const { t } = useTranslation();
  const labelId = `${optionId}-label`;
  const descriptionId = `${optionId}-description`;
  const plan = entry.planNeeded ? t(`console.plans.${entry.planNeeded}`) : null;
  const describedBy = [descriptionId, entry.triggerLock ? noteId : undefined].filter(Boolean).join(" ");
  return (
    // The combobox input owns the keyboard (aria-activedescendant); an option is never focused itself, so it takes pointer events only.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keys are handled by the combobox that controls this listbox
    <div
      id={optionId}
      role="option"
      tabIndex={-1}
      aria-selected={active}
      aria-disabled={entry.locked ? true : undefined}
      aria-labelledby={labelId}
      aria-describedby={describedBy}
      data-step={entry.id}
      className={cn(
        "flex min-h-10 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-fg max-[479px]:min-h-11",
        active && "bg-sunken ring-2 ring-inset ring-primary",
        entry.locked && "cursor-not-allowed text-fg-muted",
      )}
      onPointerMove={onActivate}
      onClick={onChoose}
    >
      <span aria-hidden="true" className={cn("inline-flex size-7 shrink-0 items-center justify-center rounded-md", entry.ai ? AI_TILE : TILE[entry.category])}>
        <Icon name={entry.ai ? "sparkles" : entry.icon} className="size-4" />
      </span>
      <span id={labelId} className="min-w-0 flex-1 truncate">
        {entry.label}
      </span>
      {plan ? (
        <Badge aria-hidden="true" className="shrink-0">
          <Icon name="lock" className="size-3" />
          {t("engage.workflows.planNeeded", { plan })}
        </Badge>
      ) : null}
      {entry.triggerLock ? <Icon name="lock" className="size-3.5 text-fg-subtle" /> : null}
      <span id={descriptionId} className="sr-only">
        {t(`${S}.sections.${entry.category}`)}
        {plan ? `, ${t(`${S}.locks.plan`, { plan })}` : ""}
      </span>
    </div>
  );
}
